import { useState, useEffect, useCallback } from 'react';
import { useQuery } from '@apollo/client';
import { reportError } from '@xbcb/ui-utils';
import { GET_CPSC_CITATION_CODES_PRESIGNED_URL } from 'components/CitationCodes/citationCodes.query';
import { parseCitationCodes, CitationCodeOption } from 'libs/cpsc';

export type CpscCitationCodeType = 'INCLUSION' | 'EXCLUSION';

const CACHE_KEY_PREFIX = 'cpsc-citation-url';

interface UrlCacheEntry {
  url: string;
  expiresAt: number;
}

// localStorage helpers for URL caching
const getLocalStorageKey = (codeType: string) =>
  `${CACHE_KEY_PREFIX}-${codeType}`;

const getUrlFromLocalStorage = (codeType: string): UrlCacheEntry | null => {
  try {
    const key = getLocalStorageKey(codeType);
    const cached = localStorage.getItem(key);
    if (!cached) return null;

    const entry: UrlCacheEntry = JSON.parse(cached);
    // Check if URL is still valid
    if (Date.now() >= entry.expiresAt) {
      localStorage.removeItem(key);
      return null;
    }

    return entry;
  } catch {
    return null;
  }
};

const setUrlToLocalStorage = (codeType: string, entry: UrlCacheEntry) => {
  try {
    const key = getLocalStorageKey(codeType);
    localStorage.setItem(key, JSON.stringify(entry));
  } catch (err) {
    reportError(err);
  }
};

const clearUrlFromLocalStorage = (codeType: string) => {
  try {
    localStorage.removeItem(getLocalStorageKey(codeType));
  } catch (err) {
    reportError(err);
  }
};

interface UseCpscCitationCodesResponse {
  options: CitationCodeOption[];
  loading: boolean;
  error: Error | undefined;
  refresh: () => Promise<void>;
}

// Hook to fetch CPSC citation codes from S3 via presigned URL
export const useCpscCitationCodes = (
  codeType: CpscCitationCodeType,
): UseCpscCitationCodesResponse => {
  const [options, setOptions] = useState<CitationCodeOption[]>([]);
  const [fetchingJson, setFetchingJson] = useState(false);
  const [jsonError, setJsonError] = useState<Error | undefined>();
  const [hasFetched, setHasFetched] = useState(false);

  // Check localStorage for cached URL on initial render only
  const [initialCachedUrl] = useState(() => getUrlFromLocalStorage(codeType));
  const hasValidCachedUrl = initialCachedUrl !== null;

  // Skip GraphQL query if we have a valid cached URL in localStorage
  const {
    data,
    loading: queryLoading,
    error: queryError,
    refetch,
  } = useQuery(GET_CPSC_CITATION_CODES_PRESIGNED_URL, {
    variables: { codeType },
    fetchPolicy: 'network-only',
    skip: hasValidCachedUrl,
  });

  // Fetch JSON from presigned URL
  const fetchJsonFromUrl = useCallback(async (url: string) => {
    setFetchingJson(true);
    setJsonError(undefined);
    try {
      const response = await fetch(url);
      if (!response.ok) {
        throw new Error(
          `Failed to fetch citation codes: ${response.statusText}`,
        );
      }
      const json = await response.json();
      const parsedOptions = parseCitationCodes(json);
      setOptions(parsedOptions);
    } catch (err) {
      reportError(err);
      setJsonError(err instanceof Error ? err : new Error('Unknown error'));
    } finally {
      setFetchingJson(false);
    }
  }, []);

  // Effect: Use cached URL or handle new GraphQL data (run once)
  useEffect(() => {
    if (hasFetched) return;

    if (hasValidCachedUrl && initialCachedUrl) {
      // Use cached URL from localStorage - fetch fresh JSON from S3
      setHasFetched(true);
      void fetchJsonFromUrl(initialCachedUrl.url);
    } else if (data?.getCpscCitationCodesPresignedUrl) {
      // New data from GraphQL query - cache URL and fetch JSON
      const { url, expiresIn } = data.getCpscCitationCodesPresignedUrl;
      setHasFetched(true);

      // Save URL to localStorage
      setUrlToLocalStorage(codeType, {
        url,
        expiresAt: Date.now() + expiresIn * 1000,
      });

      void fetchJsonFromUrl(url);
    }
  }, [
    hasFetched,
    hasValidCachedUrl,
    initialCachedUrl,
    data,
    codeType,
    fetchJsonFromUrl,
  ]);

  // Refresh handler - clears localStorage cache and refetches everything
  const refresh = useCallback(async () => {
    // Clear localStorage to force new GraphQL query
    clearUrlFromLocalStorage(codeType);
    setHasFetched(false);

    // Refetch GraphQL query to get new presigned URL
    const result = await refetch();
    if (result.data?.getCpscCitationCodesPresignedUrl) {
      const { url, expiresIn } = result.data.getCpscCitationCodesPresignedUrl;

      // Save new URL to localStorage
      setUrlToLocalStorage(codeType, {
        url,
        expiresAt: Date.now() + expiresIn * 1000,
      });

      setHasFetched(true);
      await fetchJsonFromUrl(url);
    }
  }, [codeType, fetchJsonFromUrl, refetch]);

  return {
    options,
    loading: queryLoading || fetchingJson,
    error: queryError || jsonError,
    refresh,
  };
};
